import React, { useState, useEffect, useCallback } from 'react';
import { documentConfigAPI } from '../../services/documentConfigAPI';
import { toast } from 'react-hot-toast';

// Estilos CSS en línea para animaciones
const styles = `
  @keyframes fadeIn {
    from { 
      opacity: 0; 
      transform: translateY(-10px); 
    }
    to { 
      opacity: 1; 
      transform: translateY(0); 
    }
  }
`;

const UserDocumentTypesModal = ({ show, onClose, selectedUser }) => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFields, setExpandedFields] = useState({});

  const loadUserDocumentTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await documentConfigAPI.getUserDocumentTypesAdmin(selectedUser.id);
      setDocumentTypes(response.items || []);
    } catch (error) {
      console.error('Error al cargar tipos de documentos:', error);
      toast.error('Error al cargar tipos de documentos del usuario');
      setDocumentTypes([]);
    } finally {
      setLoading(false);
    }
  }, [selectedUser?.id]);

  useEffect(() => {
    if (show && selectedUser) {
      loadUserDocumentTypes();
      setExpandedFields({}); // Reset expanded state when modal opens
    }
  }, [show, selectedUser, loadUserDocumentTypes]);

  const toggleFieldsExpansion = (docTypeId) => {
    setExpandedFields(prev => ({
      ...prev,
      [docTypeId]: !prev[docTypeId]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeIcon = (type) => {
    // Puedes personalizar los iconos según el tipo de documento
    return 'bi-file-earmark-text';
  };

  if (!show) return null;

  return (
    <>
      <style>{styles}</style>
      <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-file-earmark-text me-2"></i>
              Tipos de Documentos - {selectedUser?.full_name}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary"></div>
                <p className="mt-2 text-muted">Cargando tipos de documentos...</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted">
                      <i className="bi bi-person me-1"></i>
                      Usuario: <strong>{selectedUser?.email}</strong>
                    </span>
                    <span className="badge bg-primary">
                      {documentTypes.length} tipo{documentTypes.length !== 1 ? 's' : ''} de documento{documentTypes.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {documentTypes.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-file-earmark-x display-6 text-muted mb-3"></i>
                    <h6 className="text-muted">No hay tipos de documentos</h6>
                    <p className="text-muted mb-0">
                      Este usuario no ha creado ningún tipo de documento personalizado.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Nombre</th>
                          <th>Descripción</th>
                          <th>Campos</th>
                          <th>Creado</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentTypes.map((docType) => (
                          <React.Fragment key={docType.id}>
                            <tr>
                              <td>
                                <div className="d-flex align-items-center">
                                  <i className={`bi ${getDocumentTypeIcon(docType.nombre)} me-2 text-primary`}></i>
                                  <div className="fw-medium">{docType.nombre}</div>
                                </div>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {docType.descripcion || 'Sin descripción'}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-info">
                                    {docType.campos?.length || 0} campo{(docType.campos?.length || 0) !== 1 ? 's' : ''}
                                  </span>
                                  {docType.campos && docType.campos.length > 0 && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => toggleFieldsExpansion(docType.id)}
                                      title={expandedFields[docType.id] ? 'Ocultar campos' : 'Ver campos'}
                                    >
                                      <i className={`bi ${expandedFields[docType.id] ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(docType.created_at)}
                                </small>
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  {docType.is_template && (
                                    <span className="badge bg-primary">Plantilla</span>
                                  )}
                                  {docType.is_public && (
                                    <span className="badge bg-success">Público</span>
                                  )}
                                  {!docType.is_template && !docType.is_public && (
                                    <span className="badge bg-secondary">Privado</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* Fila expandible para mostrar los campos */}
                            {expandedFields[docType.id] && docType.campos && docType.campos.length > 0 && (
                              <tr>
                                <td colSpan="5" className="p-0">
                                  <div className="bg-light border-top" style={{ 
                                    animation: 'fadeIn 0.3s ease-in-out',
                                    borderLeft: '3px solid #0d6efd'
                                  }}>
                                    <div className="p-3">
                                      <h6 className="mb-3 text-muted d-flex align-items-center">
                                        <i className="bi bi-list-ul me-2"></i>
                                        Campos del documento ({docType.campos.length})
                                      </h6>
                                      <div className="row g-2">
                                        {docType.campos
                                          .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                                          .map((campo, index) => (
                                            <div key={campo.id} className="col-md-6">
                                              <div className="card shadow-sm h-100" style={{ 
                                                border: '1px solid #e3f2fd',
                                                borderRadius: '8px'
                                              }}>
                                                <div className="card-body p-3">
                                                  <div className="d-flex align-items-start">
                                                    <span className="badge bg-primary me-2 mt-1 fs-6">
                                                      {campo.orden || index + 1}
                                                    </span>
                                                    <div className="flex-grow-1">
                                                      <div className="fw-medium text-primary mb-1">
                                                        {campo.nombre}
                                                      </div>
                                                      <small className="text-muted d-block">
                                                        {campo.especificacion}
                                                      </small>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {documentTypes.length > 0 && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Información:</strong> Estos son los tipos de documentos personalizados que ha creado este usuario.
                      No incluye las plantillas públicas del sistema.
                    </small>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserDocumentTypesModal;
